/* Hallmark · component: Progress · genre: modern-minimal · theme: maeiz-tokens */
import React from "react";
import { clsx } from "clsx";

export interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
    value: number;
    max?: number;
    label?: string;
    tone?: "primary" | "gold" | "success" | "warning" | "danger";
    size?: "sm" | "md" | "lg";
    showValue?: boolean;
}

const toneBar: Record<NonNullable<ProgressProps["tone"]>, string> = {
    primary: "bg-primary",
    gold: "bg-gold",
    success: "bg-success",
    warning: "bg-warning",
    danger: "bg-danger",
};

const sizeBar: Record<NonNullable<ProgressProps["size"]>, string> = {
    sm: "h-1.5",
    md: "h-2.5",
    lg: "h-3.5",
};

export const Progress: React.FC<ProgressProps> = ({
    value,
    max = 100,
    label,
    tone = "primary",
    size = "md",
    showValue = true,
    className,
    ...props
}) => {
    const pct = Math.min(100, Math.max(0, (value / max) * 100));
    return (
        <div className={clsx("w-full", className)} {...props}>
            {label || showValue ? (
                <div className="mb-1.5 flex justify-between gap-3 text-xs">
                    {label ? <span className="font-medium text-neutral-dark dark:text-white">{label}</span> : <span />}
                    {showValue ? (
                        <span className="font-mono tabular-nums text-neutral-secondary">{Math.round(pct)}%</span>
                    ) : null}
                </div>
            ) : null}
            <div
                role="progressbar"
                aria-valuenow={value}
                aria-valuemin={0}
                aria-valuemax={max}
                aria-label={label}
                className={clsx(
                    "overflow-hidden rounded-full bg-neutral-border/70 dark:bg-white/10",
                    sizeBar[size],
                )}
            >
                <div
                    className={clsx(
                        "h-full rounded-full transition-[width] duration-300 ease-out motion-reduce:transition-none",
                        toneBar[tone],
                    )}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
};
